import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '@/api/ApiContext';
import type { GetBranchApiTokenResponse } from '@/api/handlers/resources/branches/responses';
import type { ApiResponse } from '@/api/handlers/responses';
import { BranchQueryKeys } from './branchQueryKeys';

type ApiTokenCache = ApiResponse<GetBranchApiTokenResponse> | undefined;

/**
 * Revokes a branch's POS API token. On success:
 *
 * 1. Invalidate the `/details` pages so the table chip flips back to
 *    "No token" on refetch.
 * 2. Patch the per-branch `apiToken` query (BOS-77/79) to the empty
 *    `{ token: null, setAt: null }` shape so a re-opened modal doesn't
 *    flash a stale plaintext from the prior session.
 */
export const useRevokeBranchApiTokenMutation = () => {
    const { apiClient } = useApi();
    const queryClient = useQueryClient();

    return useMutation<ApiResponse<void>, Error, string>({
        mutationFn: (branchId) => apiClient.branches.revokeApiToken(branchId),
        onSuccess: (result, branchId) => {
            if (!result.success) {
                return;
            }
            queryClient.invalidateQueries({
                queryKey: BranchQueryKeys.details(),
            });
            const cleared: ApiResponse<GetBranchApiTokenResponse> = {
                success: true,
                response: { branchId, tokenPreview: null, setAt: null },
            };
            queryClient.setQueryData<ApiTokenCache>(
                BranchQueryKeys.apiToken(branchId),
                cleared,
            );
        },
    });
};
